import { useCallback, useEffect, useState } from 'react'
import { Receipt, Clock, CheckCircle2, AlertTriangle, HandCoins } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { KpiCard } from '@/components/ui/KpiCard'
import { toast } from '@/components/ui/Toast'
import { brl, dateBR } from '@/lib/format'
import { RevenuesService, type EarningsReport } from '@/services/modules/RevenuesService'
import { currentMonthRange, ReportFilters, BarList } from './reportShared'

export function RevenueReportEarningsPage() {
  const init = currentMonthRange()
  const [from, setFrom] = useState(init.from)
  const [to, setTo] = useState(init.to)
  const [data, setData] = useState<EarningsReport | null>(null)
  const [loading, setLoading] = useState(false)

  const load = useCallback(async (f: string, t: string) => {
    if (!f || !t) return
    setLoading(true)
    try {
      setData(await RevenuesService.reportsEarnings(f, t))
    } catch { toast.error('Erro ao carregar o relatório de ganhos.') }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load(init.from, init.to) }, [load, init.from, init.to])

  return (
    <>
      <PageHeader title="Ganhos" subtitle="Faturamento e recebimentos no período"
        crumbs={[{ label: 'Financeiro' }, { label: 'Relatórios' }, { label: 'Ganhos' }]}
      />
      <ReportFilters from={from} to={to} onFrom={setFrom} onTo={setTo} onApply={() => load(from, to)} loading={loading} />

      {!data ? (
        <div className="text-sm text-gray-500">{loading ? 'Carregando…' : 'Sem dados.'}</div>
      ) : (
        <div className="space-y-6">
          {/* Totais */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <KpiCard label="Faturamento" accent="brand" icon={<Receipt size={18} />} value={brl(data.totals.faturamento)} />
            <KpiCard label="Pendente" accent="amber" icon={<Clock size={18} />} value={brl(data.totals.pendente)} />
            <KpiCard label="Pago" accent="green" icon={<CheckCircle2 size={18} />} value={brl(data.totals.pago)} />
            <KpiCard label="Vencido" accent="rose" icon={<AlertTriangle size={18} />} value={brl(data.totals.vencido)} />
            <KpiCard label="Comissão a pagar" accent="violet" icon={<HandCoins size={18} />}
              value={brl(data.totals.commissionDue)} hint="Rede + direto (faturas pagas)" />
          </div>

          {/* Pagamentos por dia + forma de pagamento */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-md bg-white ring-1 ring-gray-200 p-5">
              <h3 className="mb-1 font-medium text-gray-900 text-sm">Recebido por dia</h3>
              <p className="mb-4 text-xs text-gray-500">Valor pago por dia no período</p>
              <DailyChart data={data.paymentsByDay} />
            </div>
            <div className="rounded-md bg-white ring-1 ring-gray-200 p-5">
              <h3 className="mb-1 font-medium text-gray-900 text-sm">Forma de pagamento</h3>
              <p className="mb-4 text-xs text-gray-500">Valor recebido por método</p>
              <BarList
                empty="Nenhum recebimento no período."
                data={data.byPaymentMethod.map((m) => ({ label: m.method, value: m.total, valueLabel: brl(m.total) }))}
              />
            </div>
          </div>

          {/* Recebido por tipo — associados e empresas */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-md bg-white ring-1 ring-gray-200 p-5">
              <h3 className="mb-1 font-medium text-gray-900 text-sm">Recebido por associado</h3>
              <p className="mb-4 text-xs text-gray-500">Por tipo (modalidade do plano)</p>
              <BarList
                empty="Nenhum recebimento de associados no período."
                data={data.bySubscriberType.map((t) => ({ label: t.type, value: t.total, valueLabel: brl(t.total) }))}
              />
            </div>
            <div className="rounded-md bg-white ring-1 ring-gray-200 p-5">
              <h3 className="mb-1 font-medium text-gray-900 text-sm">Recebido por empresa</h3>
              <p className="mb-4 text-xs text-gray-500">Por tipo (modalidade do plano)</p>
              <BarList
                empty="Nenhum recebimento de empresas no período."
                data={data.byCompanyType.map((t) => ({ label: t.type, value: t.total, valueLabel: brl(t.total) }))}
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

/** Gráfico de colunas compacto (SVG) para o valor recebido por dia. */
function DailyChart({ data }: { data: { date: string; total: number }[] }) {
  if (data.length === 0) return <p className="text-sm text-gray-400">Nenhum recebimento no período.</p>
  const w = 520, h = 180
  const pad = { top: 12, right: 8, bottom: 26, left: 8 }
  const innerW = w - pad.left - pad.right
  const innerH = h - pad.top - pad.bottom
  const max = Math.max(...data.map((d) => d.total), 1)
  const n = data.length
  const gap = 2
  const bw = Math.max(2, innerW / n - gap)
  const dayOf = (iso: string) => iso?.slice(8, 10) ?? ''

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" role="img" aria-label="Recebido por dia">
      {[0, 0.5, 1].map((t) => {
        const y = pad.top + innerH - t * innerH
        return <line key={t} x1={pad.left} y1={y} x2={w - pad.right} y2={y} stroke="#f1f5f9" strokeWidth={1} />
      })}
      {data.map((d, i) => {
        const x = pad.left + i * (innerW / n)
        const bh = (d.total / max) * innerH
        const y = pad.top + innerH - bh
        const showLabel = n <= 16 || i % Math.ceil(n / 16) === 0
        return (
          <g key={d.date}>
            <rect x={x} y={y} width={bw} height={bh} rx={1.5} className="fill-brand-500">
              <title>{`${dateBR(d.date)}: ${brl(d.total)}`}</title>
            </rect>
            {showLabel && (
              <text x={x + bw / 2} y={h - 8} textAnchor="middle" className="fill-gray-400" fontSize={9}>{dayOf(d.date)}</text>
            )}
          </g>
        )
      })}
    </svg>
  )
}

export default RevenueReportEarningsPage
